/**
 * Data Sources Service — TASK-851
 *
 * The client for the nine generic `/api/sources` endpoints. It knows about no vendor, and it
 * must stay that way: TASK-850's whole point is that adding a source is a manifest rather than
 * a code change, so a `if (name === 'morningstar')` here would put a frontend edit back on that
 * path. `EmailConnectionSettings` is the cautionary case — a real connect flow that cannot gain
 * a provider without a React change, because its PROVIDERS array is hardcoded.
 *
 * Every shape below mirrors `shared/sources/catalog.py::CatalogEntry.to_dict()`. The server
 * composes what exists (registry), what this account has connected, and what each still needs;
 * the page renders that composition and recomputes none of it.
 */

import { API_CONFIG } from '../config/api-config';

/** A value the tenant must supply, carrying the manifest's own label — never its value. */
export interface SourceConfigField {
  name: string;
  label: string;
  secret: boolean;
  /** True once the account has supplied it. The VALUE is never carried by the API. */
  provided: boolean;
}

export interface DataSourceCard {
  name: string;
  kind: string;
  display_name: string;
  version: string;
  transport: string;
  /** A manifest whose transport arm is not built yet. */
  implemented: boolean;
  /** What the vendor requires, as documentation. Never a gate — DEC-847-7. */
  requires: Record<string, unknown>;
  /**
   * The manifest's own sentence about what to buy and where the keys come from, rendered ABOVE
   * the config form. It replaces the `entitled` flag the page used to hide the form behind:
   * that flag was hard-coded empty server-side, so the card told a firm to add credentials and
   * rendered no field to add them into.
   */
  setup_help: string | null;
  connected: boolean;
  enabled: boolean;
  config_fields: SourceConfigField[];
  connected_at: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_error: string | null;
  available: boolean;
  unavailable_reason: string | null;
  /**
   * Who can act on `unavailable_reason` — 'operator' (deployment configuration) or 'advisor'
   * (something they can supply on this page). TASK-880: a field rather than prose the page
   * parses, because the card changes what it offers based on it.
   */
  unavailable_actor: 'operator' | 'advisor' | null;
  /**
   * Whether this card offers a config form and a Connect button. Composed server-side in
   * `catalog.py::can_connect` and rendered here, never recomputed — the page having its own
   * stricter opinion is what hid the fields `connect` requires.
   */
  can_connect: boolean;
  /**
   * A connection whose source is no longer registered on this deployment (TASK-889). The card
   * exists only so the credential can be forgotten — there is nothing to connect to, test, or
   * configure.
   */
  orphaned: boolean;
  /** Whether a credential is stored. The value never travels; this is only whether one exists. */
  has_credentials: boolean;
  /**
   * Connected, and never once completed a sync. A separate field rather than something the
   * page infers from a null date — TASK-825's rule that a connection timestamp is not a sync
   * timestamp, which is how the Morning Digest showed a confident "Quiet night" over a
   * pipeline with no inputs.
   */
  never_synced: boolean;
}

export interface ConnectResult {
  status: string;
  /** Present when the source authorizes over OAuth and the browser must be sent to the vendor. */
  authorization_url?: string;
  flow_id?: string;
  /**
   * Whether the vendor itself accepted these credentials. False means the transport has no live
   * probe yet (the `mcp` arm until TASK-854), NOT that the keys were refused — a refusal is a
   * failed request carrying the vendor's own reason.
   */
  verified?: boolean;
  verification_note?: string;
}

export interface TestResult {
  ok: boolean;
  /** Advisor-legible; the raw vendor error stays in the service log. */
  message?: string;
}

class DataSourcesService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.GATEWAY_URL;
  }

  private headers(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}/api/sources${path}`, {
      ...init,
      headers: this.headers(),
    });
    if (!response.ok) {
      // Prefer the server's own sentence. It writes the setup and availability copy, and a
      // vendor rejection arrives here as the transport's advisor note — because the manifest
      // and the vendor, not this file, know how to name a vendor's product and its refusal.
      let detail = `Request failed (${response.status})`;
      try {
        const body = await response.json();
        if (body?.detail) detail = String(body.detail);
      } catch {
        /* a non-JSON body is not worth a second failure */
      }
      throw new Error(detail);
    }
    return response.json() as Promise<T>;
  }

  /** What exists, and what each still needs. Unconnected sources appear too — otherwise the page could never connect the first one. */
  async getCatalog(kind?: string): Promise<DataSourceCard[]> {
    const query = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    const data = await this.request<{ sources: DataSourceCard[] }>(`/catalog${query}`);
    return data.sources ?? [];
  }

  async getStatus(name: string): Promise<DataSourceCard> {
    return this.request<DataSourceCard>(`/${encodeURIComponent(name)}/status`);
  }

  /** Begin a connection. `config` carries the manifest's declared fields, never a stored secret. */
  async connect(name: string, config: Record<string, string>): Promise<ConnectResult> {
    return this.request<ConnectResult>(`/${encodeURIComponent(name)}/connect`, {
      method: 'POST',
      body: JSON.stringify({ config }),
    });
  }

  async test(name: string): Promise<TestResult> {
    return this.request<TestResult>(`/${encodeURIComponent(name)}/test`, { method: 'POST' });
  }

  /**
   * Toggle without disconnecting — "enabled" and "connected" are different states.
   *
   * Returns what the endpoint actually returns. It was typed `DataSourceCard`, which no caller
   * had yet dereferenced — a silent lie that would have become `undefined` at runtime for the
   * first one that did.
   */
  async setEnabled(name: string, enabled: boolean): Promise<{ status: string; enabled: boolean }> {
    return this.request<{ status: string; enabled: boolean }>(`/${encodeURIComponent(name)}/enabled`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async disconnect(name: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(`/${encodeURIComponent(name)}`, { method: 'DELETE' });
  }
}

export const dataSourcesService = new DataSourcesService();
export default dataSourcesService;
